import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import { Pencil, Trash2, Eye, Filter, RotateCw, FilePlus2, Printer } from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => Number(n || 0).toLocaleString("id-ID");

const parseMaterialMasuk = (raw) => {
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw || "[]") : raw;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export default function DataPage() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ tanggal: "", shift: "all", team_operasi: "all" });
  const [toDelete, setToDelete] = useState(null);
  const [preview, setPreview] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.tanggal) params.tanggal = filters.tanggal;
      if (filters.shift && filters.shift !== "all") params.shift = filters.shift;
      if (filters.team_operasi && filters.team_operasi !== "all") params.team_operasi = filters.team_operasi;
      const { data } = await api.get("/reports", { params });
      setRows(data);
    } catch (e) {
      toast.error("Gagal memuat data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filters]);

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/reports/${toDelete}`);
      toast.success("Laporan dihapus");
      setToDelete(null);
      load();
    } catch (e) {
      toast.error("Gagal menghapus");
    }
  };

  const openPreview = async (id) => {
    try {
      const { data } = await api.get(`/reports/${id}`);
      setPreview(data);
    } catch { toast.error("Gagal memuat detail"); }
  };

  const openPrint = (tanggal) => {
    if (!tanggal) {
      toast.error("Tanggal laporan tidak tersedia");
      return;
    }
    window.open(`/print/daily/${tanggal}`, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="p-4 sm:p-6 max-w-[1600px] mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5">
        <div>
          <div className="label-ind">Manajemen Data</div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Data Laporan</h1>
          <p className="text-sm text-slate-500 mt-1">Semua laporan tersimpan langsung di Google Sheets.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} data-testid="btn-refresh" className="rounded-sm">
            <RotateCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
          <Button onClick={() => nav("/input")} data-testid="btn-new-report" className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white">
            <FilePlus2 className="w-4 h-4 mr-1" /> Laporan Baru
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="rounded-sm border-slate-200 bg-white p-4 mb-4">
        <div className="flex items-center gap-3 mb-3">
          <Filter className="w-4 h-4 text-slate-500" />
          <div className="label-ind">Filter</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="label-ind">Tanggal</label>
            <Input type="date" value={filters.tanggal} onChange={(e) => setFilters({ ...filters, tanggal: e.target.value })} data-testid="filter-tanggal" className="h-9 mt-1 rounded-sm" />
          </div>
          <div>
            <label className="label-ind">Shift</label>
            <Select value={filters.shift} onValueChange={(v) => setFilters({ ...filters, shift: v })}>
              <SelectTrigger className="h-9 mt-1 rounded-sm" data-testid="filter-shift"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua</SelectItem>
                <SelectItem value="1">Shift 1</SelectItem>
                <SelectItem value="2">Shift 2</SelectItem>
                <SelectItem value="3">Shift 3</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="label-ind">Team Operasi</label>
            <Select value={filters.team_operasi} onValueChange={(v) => setFilters({ ...filters, team_operasi: v })}>
              <SelectTrigger className="h-9 mt-1 rounded-sm" data-testid="filter-team"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua</SelectItem>
                <SelectItem value="E">Team E</SelectItem>
                <SelectItem value="F">Team F</SelectItem>
                <SelectItem value="G">Team G</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button variant="outline" onClick={() => setFilters({ tanggal: "", shift: "all", team_operasi: "all" })} data-testid="btn-reset-filter" className="h-9 rounded-sm w-full">
              Reset
            </Button>
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card className="rounded-sm border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full table-dense">
            <thead>
              <tr>
                <th className="text-left">ID Laporan</th>
                <th className="text-left">Tanggal</th>
                <th className="text-left">Shift</th>
                <th className="text-left">Team</th>
                <th className="text-left">Team IF</th>
                <th className="text-left">Staff</th>
                <th className="text-right">Casting</th>
                <th className="text-right">Pengiriman</th>
                <th className="text-right">LNG</th>
                <th className="text-right w-32">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={10} className="text-center py-10 text-slate-500">Memuat data...</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={10} className="text-center py-10 text-slate-400">Belum ada laporan. Klik "Laporan Baru" untuk menambah.</td></tr>
              )}
              {!loading && rows.map((r) => (
                <tr key={r.id_laporan} data-testid={`row-${r.id_laporan}`}>
                  <td className="font-mono text-xs text-slate-600">{r.id_laporan}</td>
                  <td>{r.tanggal}</td>
                  <td><span className="inline-block px-2 py-0.5 bg-slate-100 text-slate-700 text-xs font-semibold rounded-sm">Shift {r.shift}</span></td>
                  <td><span className="inline-block px-2 py-0.5 bg-blue-50 text-blue-700 text-xs font-semibold rounded-sm">{r.team_operasi}</span></td>
                  <td>{r.team_if}</td>
                  <td className="truncate max-w-[160px]">{r.nama_staff}</td>
                  <td className="text-right font-mono">{fmt(r.total_casting)}</td>
                  <td className="text-right font-mono">{fmt(r.total_pengiriman)}</td>
                  <td className="text-right font-mono">{fmt(r.lng_2330)}</td>
                  <td className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => openPreview(r.id_laporan)} data-testid={`btn-view-${r.id_laporan}`}>
                        <Eye className="w-4 h-4 text-slate-600" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => nav(`/input/${r.id_laporan}`)} data-testid={`btn-edit-${r.id_laporan}`}>
                        <Pencil className="w-4 h-4 text-blue-600" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => openPrint(r.tanggal)} data-testid={`btn-print-${r.id_laporan}`} title="Print laporan harian">
                        <Printer className="w-4 h-4 text-emerald-700" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => setToDelete(r.id_laporan)} data-testid={`btn-delete-${r.id_laporan}`}>
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Delete Dialog */}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus Laporan?</AlertDialogTitle>
            <AlertDialogDescription>
              Laporan <span className="font-mono">{toDelete}</span> akan dihapus dari semua sheet. Aksi ini tidak dapat dibatalkan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="btn-cancel-delete">Batal</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="btn-confirm-delete">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Preview */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Detail Laporan
              {preview && <span className="block text-xs font-mono text-slate-500 font-normal mt-1">{preview.main?.id_laporan}</span>}
            </DialogTitle>
            <DialogDescription>
              Ringkasan lengkap laporan shift termasuk Casting, Pengiriman AA, dan Problem.
            </DialogDescription>
          </DialogHeader>
          {preview && (
            <div className="space-y-4 text-sm">
              <PreviewDetails preview={preview} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PreviewDetails({ preview }) {
  const main = preview.main || {};
  const materialMasuk = parseMaterialMasuk(main.pengiriman_material_masuk_json);
  const isShift2 = String(main.shift || "") === "2";

  return (
    <>
      <SummaryGrid title="Informasi Shift" fields={[
        ["Tanggal", main.tanggal],
        ["Shift", main.shift],
        ["Schedule", main.schedule],
        ["Team Operasi", main.team_operasi],
        ["Team IF", main.team_if],
        ["Nama Staff", main.nama_staff],
      ]} />

      <DetailList title="Produksi - Casting" rows={preview.castings} cols={[{k:"no_lot",label:"No. Lot"},{k:"total",label:"Total"}]} />
      <DetailList title="Produksi - Pengiriman AA" rows={preview.pengirimans} cols={[{k:"no_lot",label:"No. Lot"},{k:"total",label:"Total"}]} />

      <SummaryGrid title="Stock Metal & Material" fields={[
        ["Kadar GC-401 (%)", main.kadar_gc401],
        ["CE (%)", main.ce],
        ["Sisa Butt (Pcs)", main.sisa_butt],
        ["AC-401 (%)", main.ac401],
        ["HF-401 (kg)", main.hf401],
        ["Masuk Metal SCA (Kg)", main.masuk_metal_sca],
        ["Rand Crust (Kg)", main.rand_crust],
        ["Ladle Dross (Kg)", main.ladle_dross],
      ]} />

      {isShift2 && (
        <>
          <SummaryGrid title="Pengiriman Material" fields={[
            ["Crust Over 30 mm (kg)", main.pengiriman_crust_over_30],
            ["Aluminium ball (kg)", main.pengiriman_aluminium_ball],
            ["Metal Scrap (kg)", main.pengiriman_metal_scrap],
            ["Dust Hammer mill (kg)", main.pengiriman_dust_hammer_mill],
            ["Aluminium Scrap (kg)", main.pengiriman_aluminium_scrap],
          ]} />
          <DetailList title="Material Masuk" rows={materialMasuk} cols={[{k:"name",label:"Nama Material"},{k:"kg",label:"Kg"}]} />
        </>
      )}

      <SummaryGrid title="Peralatan" fields={[
        ["ATC Aktif", main.atc_aktif || "-"],
        ["Available ATC", main.available_atc],
        ["Forklift Aktif", main.forklift_aktif],
        ["Sweeper Aktif", main.sweeper_aktif],
        ["HT", main.ht],
      ]} />

      <SummaryGrid title="Kondisi Butt" fields={[
        ["Butt Diterima", main.butt_diterima],
        ["Berat Rata Rata", main.berat_rata_rata],
        ["Tinggi Rata Rata", main.tinggi_rata_rata],
        ["Total Butt Tebal", main.butt_tebal],
        ["Total Butt Siluman", main.butt_siluman],
        ["Reject Sticking Kecil", main.sticking_kecil],
        ["Reject Sticking Besar", main.sticking_besar],
        ["Reject Sticking Gosong", main.sticking_gosong],
        ["Impeller 1", main.impeller_1],
        ["Impeller 2", main.impeller_2],
        ["Impeller 3", main.impeller_3],
      ]} />

      <SummaryGrid title="Data LNG" fields={[
        ["LNG 23:30 (Nm3)", main.lng_2330],
      ]} />

      <DetailList title="Problem & Countermeasure" rows={preview.problems} cols={[
        {k:"waktu_mulai",label:"Mulai"},
        {k:"waktu_selesai",label:"Selesai"},
        {k:"equipment",label:"Equipment"},
        {k:"problem",label:"Problem"},
        {k:"countermeasure",label:"Countermeasure"},
      ]} />
      <DetailList title="Informasi Lainnya" rows={preview.infos} cols={[
        {k:"judul_informasi",label:"Judul Informasi"},
        {k:"isian_informasi",label:"Isian Informasi"},
      ]} />
    </>
  );
}

function SummaryGrid({ title, fields }) {
  return (
    <div className="border border-slate-200 rounded-sm overflow-hidden">
      <div className="bg-slate-50 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-700 border-b border-slate-200">
        {title}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 p-3 text-xs">
        {fields.map(([label, val]) => (
          <div key={label} className="border-l-2 border-slate-200 pl-2 py-1">
            <div className="label-ind">{label}</div>
            <div className="font-semibold text-slate-800">{val || "-"}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
function DetailList({ title, rows, cols }) {
  return (
    <div className="border border-slate-200 rounded-sm overflow-hidden">
      <div className="bg-slate-50 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-700 border-b border-slate-200">
        {title} <span className="text-slate-400 font-normal normal-case">({rows?.length || 0})</span>
      </div>
      <table className="w-full text-xs">
        <thead>
          <tr>
            {cols.map((c) => <th key={c.k} className="text-left px-3 py-1.5 font-semibold text-slate-600 border-b border-slate-100">{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {(!rows || rows.length === 0) && <tr><td colSpan={cols.length} className="text-center py-3 text-slate-400">Tidak ada data</td></tr>}
          {rows?.map((r, i) => (
            <tr key={i}>
              {cols.map((c) => <td key={c.k} className="px-3 py-1.5 border-b border-slate-50 text-slate-700">{r[c.k] || "-"}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

